import type { SkillCard } from "../api/advisorGridApi";

/** 首屏上限(2 列 → ≤3 行)。当前 catalog 老板面基线 5 张,留 1~3 个场景置顶名额。 */
const MAX_FIRST_SCREEN = 6;
/** 单场景最多置顶几张(留出全局 recommended 的位置,避免首屏被单一场景吃满)。 */
const MAX_PINNED = 3;

/**
 * 全局 recommended 首屏(D2* 原逻辑):每业务域取 recommended 前 perCategory 个。
 * 老后端不回 recommended(全 falsy)→ 全部进首屏(不逼出空首屏)。
 */
function baseFirstScreenNames(cards: SkillCard[], perCategory: number): Set<string> {
  const recommended = cards.filter((c) => c.recommended);
  if (recommended.length === 0) return new Set(cards.map((c) => c.name));
  const perCat = new Map<string, number>();
  const names = new Set<string>();
  for (const c of recommended) {
    const cat = c.category || "其他";
    const n = perCat.get(cat) ?? 0;
    if (n < perCategory) {
      names.add(c.name);
      perCat.set(cat, n + 1);
    }
  }
  return names;
}

/**
 * 场景置顶:把 hint 里**后端真下发了**的卡按 hint 顺序取前 MAX_PINNED 张。
 * join 不上的名字(HIDDEN / 无权限 / 租户 disabled)静默丢弃 —— 前端只重排,绝不凭空造卡。
 */
function pinnedNames(cards: SkillCard[], sceneSkills: string[]): string[] {
  const present = new Set(cards.map((c) => c.name));
  return sceneSkills.filter((n) => present.has(n)).slice(0, MAX_PINNED);
}

/**
 * skill 网格首屏渐进披露(D2* 强化 2026-07-10;场景置顶 2026-07-15)。
 *
 * <p>skill 变多后「首屏铺全部 recommended」会淹没(老板面曾 9 张平铺)。分两层:
 * ①**场景置顶** —— 用户当前所在页面对应的 skill(sceneSkills,来自 shared/lib/advisorScene)排最前;
 * ②**全局 recommended** —— 每业务域取前 perCategory 个补位。二者去重后取前 MAX_FIRST_SCREEN 张进首屏,
 * 其余按 category 折叠进「更多技能」。
 *
 * <p>★sceneSkills 缺省为空 → 行为与场景化之前逐字一致(工作台等无场景页走此路径)。
 * ★场景置顶只**重排**入参里已有的卡,不新增、不解锁 —— 权限过滤是后端 /advisor/skills 的职责。
 *
 * <p>返回保持入参顺序(后端已按 category+sort 稳定排序),置顶段除外(按 hint 优先级排)。
 */
export function splitSkillCards(
  cards: SkillCard[],
  perCategory = 2,
  sceneSkills: string[] = []
): { firstScreen: SkillCard[]; more: SkillCard[] } {
  const pinned = pinnedNames(cards, sceneSkills);
  const base = baseFirstScreenNames(cards, perCategory);
  // 置顶在前、其余按后端顺序补位,去重后截断到首屏上限。
  const firstNames = [
    ...new Set([...pinned, ...cards.filter((c) => base.has(c.name)).map((c) => c.name)]),
  ].slice(0, MAX_FIRST_SCREEN);
  const rank = new Map(firstNames.map((n, i) => [n, i]));
  return {
    firstScreen: cards
      .filter((c) => rank.has(c.name))
      .sort((a, b) => rank.get(a.name)! - rank.get(b.name)!),
    more: cards.filter((c) => !rank.has(c.name)),
  };
}
